import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import "./whyChooseUs.css";

const benefits = [
  {
    id: "speed",
    number: "01",
    title: "Speed and Accuracy",
    description:
      "Domain-trained expert annotators and daily throughput tracking against SLA targets.",
    metric: { label: "SLA TRACKING", value: "DAILY", fill: 1 },
  },
  {
    id: "quality",
    number: "02",
    title: "Trustworthy Output",
    description:
      "A rigorous multi-level quality assurance process that ensures a 97%+ quality benchmark, validated on every project.",
    metric: { label: "QUALITY BENCHMARK", value: "97%+", fill: 0.97 },
  },
  {
    id: "scale",
    number: "03",
    title: "Rapid Scale-Up",
    description:
      "A risk-free 2-day pilot before full production, and team deployment within 24 business hours of sign-off. You see our work before you commit to volume.",
    metric: { label: "TEAM DEPLOYMENT", value: "24 BIZ HRS", fill: 1 },
  },
  {
    id: "workflow",
    number: "04",
    title: "Customised Workflow",
    description:
      "Every engagement is tailored to your goals, with annotation guidelines, QA, and delivery cadence adapted to your project.",
    metric: { label: "GUIDELINES + QA", value: "CUSTOM", fill: 1 },
  },
  {
    id: "cost",
    number: "05",
    title: "Lower Total Cost",
    description:
      "Pay-per-verified-productive-hour billing. You are not paying for our learning curve or mistakes, our internal rework, or idle bench time.",
    metric: { label: "BILLING MODEL", value: "PER VERIFIED HR", fill: 1 },
  },
  {
    id: "security",
    number: "06",
    title: "Data Security",
    description:
      "ISO/IEC 27001:2022, ISO 9001:2015, TISAX Level 3, HIPAA, and GDPR compliance. Biometric access, 24*7 CCTV monitoring, and policies and procedures that prohibit cameras and unauthorised storage devices on the floor.",
    metric: { label: "FLOOR MONITORING", value: "24×7", fill: 1 },
  },
];

function LedgerRow({ benefit, index, isActive, isDone, rowRef }) {
  const { metric } = benefit;

  return (
    <li
      ref={rowRef}
      style={{ "--i": index }}
      className={`why-ledger__row${isActive ? " is-active" : ""}${
        isDone ? " is-done" : ""
      }`}
    >
      <span className="why-ledger__number" aria-hidden="true">
        {benefit.number}
      </span>

      <div className="why-ledger__copy">
        <h3>{benefit.title}</h3>
        <p>{benefit.description}</p>
      </div>

      <div className="why-ledger__metric" aria-hidden="true">
        <div className="why-ledger__metric-head">
          <s>CLAIM</s>
          <span>VERIFIED</span>
        </div>
        <div className="why-ledger__bar">
          <span style={{ "--fill": metric.fill }} />
        </div>
        <div className="why-ledger__metric-foot">
          <span>{metric.label}</span>
          <strong>{metric.value}</strong>
        </div>
      </div>
    </li>
  );
}

const REACH = 0.6;

export default function WhyChooseUs() {
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  const rowRefs = useRef([]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    let lastWidth = 0;

    const measure = () => {
      list.style.setProperty("--card-h", "0px");

      const tallest = Math.max(
        0,
        ...rowRefs.current.filter(Boolean).map((row) => row.offsetHeight),
      );

      list.style.setProperty("--card-h", `${tallest}px`);
    };

    const observer = new ResizeObserver(() => {
      const width = list.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      measure();
    });

    measure();
    lastWidth = list.clientWidth;
    observer.observe(list);
    document.fonts?.ready.then(measure);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      const limit = window.innerHeight * REACH;
      let current = -1;

      rowRefs.current.forEach((row, index) => {
        if (row && row.getBoundingClientRect().top < limit) current = index;
      });

      setActiveIndex((previous) => (previous === current ? previous : current));
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section className="why-choose" aria-labelledby="why-choose-title">
      <div className="why-choose__inner">
        <header className="why-choose__header">
          <span className="why-choose__eyebrow">
            <span className="why-choose__eyebrow-dot" aria-hidden="true" />
            Best Practices
            <span className="why-choose__eyebrow-line" aria-hidden="true" />
          </span>
          <h2 id="why-choose-title">
            Why Leading AI Teams Choose <em>Qualitas Global</em>.
          </h2>

        </header>

        <ol className="why-ledger" ref={listRef}>
          {benefits.map((benefit, index) => (
            <LedgerRow
              key={benefit.id}
              benefit={benefit}
              index={index}
              isActive={activeIndex === index}
              isDone={index <= activeIndex}
              rowRef={(element) => {
                rowRefs.current[index] = element;
              }}
            />
          ))}
        </ol>

        <div className="why-choose__actions">
          <Link
            to="/contact-us"
            className="why-choose__button why-choose__button--primary"
          >
            Talk to Our Team
            <ArrowUpRight size={17} strokeWidth={1.8} aria-hidden="true" />
          </Link>
          <Link
            to="/why-us"
            className="why-choose__button why-choose__button--secondary"
          >
            Know More
            <ArrowUpRight size={17} strokeWidth={1.8} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
